import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Button,
  Avatar,
  Popover,
  Link,
  useTheme,
  useMediaQuery,
  Drawer,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  School as SchoolIcon,
  BugReport as BugReportIcon,
  NotificationsNoneOutlined,
  NotificationsNone as NotificationsNoneIcon
} from '@mui/icons-material';

interface NotificationPanelProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onViewAll: () => void;
  onNotificationClick: (event: React.MouseEvent<HTMLElement>) => void;
  notifications?: any;
  iconSize?: 'small' | 'medium';
  iconSx?: object;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  anchorEl,
  open,
  onClose,
  onViewAll,
  onNotificationClick,
  notifications = [],
  iconSize = 'medium',
  iconSx = {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const notificationCount = notifications.length;
  const isDarkMode = theme.palette.mode === 'dark';
  const hasNotifications = notifications.length > 0;

  const Header = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        borderBottom: `1px solid ${theme.palette.navigation.borderColor}`,
        backgroundColor: theme.palette.navigation.background
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: isMobile ? 20 : 18,
          fontWeight: 600,
          color: theme.palette.navigation.text
        }}
      >
        Notifications
      </Typography>
      <IconButton
        onClick={onClose}
        sx={{
          color: theme.palette.navigation.text,
          backgroundColor: isMobile ? theme.palette.navigation.hoverBackground : 'transparent',
          '&:hover': {
            backgroundColor: theme.palette.navigation.hoverBackground
          }
        }}
      >
        <CloseIcon fontSize={isMobile ? 'medium' : 'small'} />
      </IconButton>
    </Box>
  );

  const NotificationCard = ({ notification }: { notification: any }) => (
    <Card
      sx={{
        my: 1.5,
        boxShadow: 'none',
        borderRadius: '7px',
        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#ECEFF5',
        border: isDarkMode ? `1px solid ${theme.palette.navigation.borderColor}` : 'none',
        '&:first-of-type': { mt: 1.5 },
        '&:last-of-type': { mb: 1.5 }
      }}
    >
      <CardContent sx={{ display: 'flex', gap: 2, p: '16px !important' }}>
        <Avatar
          sx={{
            bgcolor: isDarkMode ? theme.palette.primary.main : '#0c267c',
            width: 40,
            height: 40,
            mt: 0.5,
            flexShrink: 0
          }}
        >
          {notification.icon}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              pb: 1,
              borderBottom: `1px solid ${theme.palette.navigation.borderColor}`,
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 0.5 : 0
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.3,
                color: theme.palette.text.primary
              }}
            >
              {notification.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: 12,
                color: theme.palette.text.secondary,
                ml: isMobile ? 0 : 2
              }}
            >
              {notification.time}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              lineHeight: 1.4,
              mt: 1,
              mb: notification.type === 'action' ? 1.5 : 0,
              color: theme.palette.text.secondary
            }}
          >
            {notification.message}
          </Typography>

          {notification.type === 'action' && notification.actionLabel && (
            <Link href={notification.actionLink} underline="none">
              <Button
                variant="contained"
                size="small"
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: 13,
                  px: 2,
                  py: 0.5,
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#DCE3F0',
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#c8d5e8'
                  }
                }}
              >
                {notification.actionLabel}
              </Button>
            </Link>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const EmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center',
        minHeight: isMobile ? 300 : 200
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3
        }}
      >
        <NotificationsNoneIcon
          sx={{
            fontSize: 40,
            color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : '#bdbdbd'
          }}
        />
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontSize: 18,
          fontWeight: 600,
          color: theme.palette.text.primary,
          mb: 1
        }}
      >
        No notifications yet
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontSize: 14,
          color: theme.palette.text.secondary,
          lineHeight: 1.5,
          maxWidth: 280
        }}
      >
        When you have new notifications, they'll appear here to keep you updated.
      </Typography>
    </Box>
  );

  const Footer = () => {
    if (!hasNotifications) return null;

    return (
      <Box
        sx={{
          p: 2,
          textAlign: 'center',
          backgroundColor: theme.palette.navigation.background,
          borderTop: `1px solid ${theme.palette.navigation.borderColor}`
        }}
      >
        <Button
          sx={{
            color: isDarkMode ? theme.palette.primary.main : '#001C54',
            textTransform: 'none',
            fontSize: 16,
            minWidth: 100,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: theme.palette.navigation.hoverBackground
            }
          }}
          onClick={onViewAll}
        >
          View All
        </Button>
      </Box>
    );
  };

  const Content = () => (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        px: hasNotifications ? 2 : 0,
        maxHeight: isMobile ? 'none' : 480,
        backgroundColor: theme.palette.background.default
      }}
    >
      {hasNotifications ? (
        notifications.map((notification: any) => <NotificationCard key={notification.id} notification={notification} />)
      ) : (
        <EmptyState />
      )}
    </Box>
  );

  const Container = ({ children }: { children: React.ReactNode }) => (
    <Box
      sx={{
        width: isMobile ? '100vw' : 400,
        height: isMobile ? '90vh' : 'auto',
        maxHeight: isMobile ? '90vh' : hasNotifications ? 600 : 400,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper
      }}
    >
      {children}
    </Box>
  );

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton size={iconSize} sx={iconSx} onClick={onNotificationClick}>
          <Badge badgeContent={hasNotifications ? notificationCount : 0} color="error">
            <NotificationsNoneOutlined fontSize={iconSize} />
          </Badge>
        </IconButton>
      </Tooltip>

      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              height: '90vh',
              backgroundColor: theme.palette.background.paper
            }
          }}
        >
          <Container>
            <Header />
            <Content />
            <Footer />
          </Container>
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '12px',
              backgroundColor: theme.palette.background.paper,
              border: isDarkMode ? `1px solid ${theme.palette.navigation.borderColor}` : 'none'
            }
          }}
        >
          <Container>
            <Header />
            <Content />
            <Footer />
          </Container>
        </Popover>
      )}
    </>
  );
};

export default NotificationPanel;
